<template>
  <div class="multi-select-zone">
    <div class="zone-selector">
      <select v-model="selectedZoneId" @change="handleZoneSelect" class="zone-dropdown">
        <option disabled value="">Select Zone</option>
        <option v-for="zone in zones" :key="zone.id" :value="zone.id">{{ zone.zonename }}</option>
      </select>
      <button @click="addZone" :disabled="!selectedZoneId || (selectedZones.length > 0 && !isNoObservationMode)" class="add-zone-btn">Add Zone</button>
    </div>
    
    <div v-if="selectedZones.length > 0" class="selected-zones">
      <div v-for="(zone, index) in selectedZones" :key="index" class="zone-tag">
        <span>{{ zone.name }}</span>
        <button @click="removeZone(index)" class="remove-zone-btn">&times;</button>
      </div>
    </div>
    <div v-else class="no-zones-selected">
      No zones selected
    </div>
    
    <div v-if="selectedZones.length > 1 && !isNoObservationMode" class="validation-warning">
      Multiple zones are only allowed for "No Observation" reports
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultiSelectZone',
  props: {
    zones: {
      type: Array,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    isNoObservationMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedZoneId: '',
      selectedZones: this.value || []
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.selectedZones = newVal || [];
      },
      deep: true
    },
    isNoObservationMode(newVal) {
      // If switching from "No Observation" mode to wildlife observation mode
      // and multiple zones are selected, keep only the first zone
      if (!newVal && this.selectedZones.length > 1) {
        const firstZone = this.selectedZones[0];
        this.selectedZones = [firstZone];
        this.$emit('input', [firstZone]);
      }
    }
  },
  methods: {
    handleZoneSelect() {
      // This method is triggered when a zone is selected from the dropdown
      // but doesn't add it yet - that happens in addZone
    },
    addZone() {
      if (!this.selectedZoneId) return;
      
      // Find the selected zone from the zones prop
      const selectedZone = this.zones.find(zone => zone.id === this.selectedZoneId);
      if (!selectedZone) return;
      
      // Check if the zone is already selected
      const isDuplicate = this.selectedZones.some(zone => zone.id === selectedZone.id);
      if (isDuplicate) {
        // Optionally show a message that the zone is already selected
        return;
      }
      
      // If not in "No Observation" mode and already have a zone selected,
      // replace the existing zone instead of adding a new one
      if (!this.isNoObservationMode && this.selectedZones.length > 0) {
        const newZone = {
          id: selectedZone.id,
          name: selectedZone.zonename
        };
        
        this.selectedZones = [newZone];
        this.$emit('input', [newZone]);
        
        // Emit notification to parent component
        this.$parent.$emit('show-notification', {
          type: 'info',
          message: 'For wildlife observations, only one zone can be selected. Previous zone has been replaced.'
        });
      } else {
        // Add the zone to the selected zones
        const newZone = {
          id: selectedZone.id,
          name: selectedZone.zonename
        };
        
        const updatedZones = [...this.selectedZones, newZone];
        this.selectedZones = updatedZones;
        
        // Emit the updated zones to the parent component
        this.$emit('input', updatedZones);
      }
      
      // Reset the dropdown
      this.selectedZoneId = '';
    },
    removeZone(index) {
      // Remove the zone at the specified index
      const updatedZones = [...this.selectedZones];
      updatedZones.splice(index, 1);
      this.selectedZones = updatedZones;
      
      // Emit the updated zones to the parent component
      this.$emit('input', updatedZones);
    }
  }
};
</script>

<style scoped>
.multi-select-zone {
  margin-bottom: 10px;
}

.zone-selector {
  display: flex;
  margin-bottom: 5px;
}

.zone-dropdown {
  flex-grow: 1;
  padding: 5px;
  margin-right: 5px;
}

.add-zone-btn {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 3px;
}

.add-zone-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.selected-zones {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 5px;
}

.zone-tag {
  display: flex;
  align-items: center;
  background-color: #f1f1f1;
  padding: 3px 8px;
  border-radius: 3px;
  font-size: 0.9em;
}

.remove-zone-btn {
  background: none;
  border: none;
  color: #ff0000;
  cursor: pointer;
  font-size: 1.2em;
  margin-left: 5px;
  padding: 0 5px;
}

.no-zones-selected {
  color: #888;
  font-style: italic;
  margin-top: 5px;
}

.validation-warning {
  color: #ff6600;
  font-size: 0.8em;
  margin-top: 5px;
  padding: 3px 5px;
  background-color: #fff3e0;
  border-left: 3px solid #ff6600;
}
</style>